import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Building2, Phone, Mail, MapPin, Globe, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };

const CTA_ACTIONS = [
  { id: 'link', label: 'Open Link' },
  { id: 'call', label: 'Call Phone' },
  { id: 'email', label: 'Send Email' },
  { id: 'whatsapp', label: 'WhatsApp Chat' },
];

const BusinessInfo = ({ profile, setProfile }) => {
  const [formData, setFormData] = useState({
    display_name: '',
    tagline: '',
    business_name: '',
    owner_name: '',
    phone: '',
    email: '',
    address: '',
    website: '',
    business_hours: {},
    cta_button: { text: 'Contact Me', action: 'link', value: '' }
  });
  const [saving, setSaving] = useState(false);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    if (profile) {
      setFormData({
        display_name: profile.display_name || '',
        tagline: profile.tagline || '',
        business_name: profile.business_name || '',
        owner_name: profile.owner_name || '',
        phone: profile.phone || '',
        email: profile.email || '',
        address: profile.address || '',
        website: profile.website || '',
        business_hours: profile.business_hours || {},
        cta_button: profile.cta_button || { text: 'Contact Me', action: 'link', value: '' }
      });
    }
  }, [profile]);

  const autoSave = useCallback(async (data) => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/profile`, data);
      setProfile(response.data);
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  }, [setProfile]);

  const handleFieldChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => autoSave(newData), 800);
  };

  const updateHours = (day, value) => {
    const newData = {
      ...formData,
      business_hours: { ...formData.business_hours, [day]: value }
    };
    setFormData(newData);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => autoSave(newData), 800);
  };

  const updateCTA = (field, value) => {
    const newData = {
      ...formData,
      cta_button: { ...formData.cta_button, [field]: value }
    };
    setFormData(newData);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => autoSave(newData), 800);
  };

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">Profile Info</h1>
          <p className="text-[#A1A1AA] mt-1">Add your business details and contact info</p>
        </div>
        {saving && (
          <span className="text-sm text-violet-400 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-violet-400 animate-pulse" /> Saving...
          </span>
        )}
      </div>

      {/* Basic Info */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl bg-[#121212] border border-[#27272A] p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-violet-400" />
          </div>
          <h2 className="font-outfit font-semibold text-lg text-white">Business Details</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label className="text-[#EDEDED]">Display Name</Label>
            <Input data-testid="display-name-input" value={formData.display_name} onChange={(e) => handleFieldChange("display_name", e.target.value)}
              className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="Faste Inc." />
            <p className="text-xs text-[#71717A] mt-1">This is the main heading shown on your profile</p>
          </div>
          <div>
            <Label className="text-[#EDEDED]">Your Name</Label>
            <Input data-testid="owner-name-input" value={formData.owner_name} onChange={(e) => handleFieldChange("owner_name", e.target.value)}
              className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="John Doe" />
          </div>
          <div>
            <Label className="text-[#EDEDED]">Business Name</Label>
            <Input data-testid="business-name-input" value={formData.business_name} onChange={(e) => handleFieldChange("business_name", e.target.value)}
              className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="FASTE INC." />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-[#EDEDED]">Tagline</Label>
            <Input data-testid="tagline-input" value={formData.tagline} onChange={(e) => handleFieldChange("tagline", e.target.value)}
              className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="Digital NFC Solutions" />
          </div>
        </div>
      </motion.div>

      {/* Contact Info */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="rounded-2xl bg-[#121212] border border-[#27272A] p-6 mb-6">
        <h2 className="font-outfit font-semibold text-lg text-white mb-4">Contact Information</h2>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[#EDEDED] flex items-center gap-2"><Phone className="w-4 h-4" /> Phone</Label>
              <Input data-testid="phone-input" value={formData.phone} onChange={(e) => handleFieldChange("phone", e.target.value)}
                className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="+1 234 567 890" />
            </div>
            <div>
              <Label className="text-[#EDEDED] flex items-center gap-2"><Mail className="w-4 h-4" /> Email</Label>
              <Input data-testid="email-input" type="email" value={formData.email} onChange={(e) => handleFieldChange("email", e.target.value)}
                className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="contact@business.com" />
            </div>
          </div>
          
          <div>
            <Label className="text-[#EDEDED] flex items-center gap-2"><MapPin className="w-4 h-4" /> Address</Label>
            <Input data-testid="address-input" value={formData.address} onChange={(e) => handleFieldChange("address", e.target.value)}
              className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="123 Business St, City, Country" />
          </div>
          
          <div>
            <Label className="text-[#EDEDED] flex items-center gap-2"><Globe className="w-4 h-4" /> Website</Label>
            <Input data-testid="website-input" value={formData.website} onChange={(e) => handleFieldChange("website", e.target.value)}
              className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="https://www.example.com" />
          </div>
        </div>
      </motion.div>

      {/* Business Hours */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="rounded-2xl bg-[#121212] border border-[#27272A] p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Clock className="w-5 h-5 text-violet-400" />
          <h2 className="font-outfit font-semibold text-lg text-white">Business Hours</h2>
        </div>
        
        <div className="space-y-3">
          {DAYS.map((day) => (
            <div key={day} className="flex items-center gap-4">
              <span className="w-24 text-sm text-[#A1A1AA]">{DAY_LABELS[day]}</span>
              <Input
                data-testid={`hours-${day}-input`}
                value={formData.business_hours[day] || ''}
                onChange={(e) => updateHours(day, e.target.value)}
                className="flex-1 bg-[#1E1E1E] border-[#27272A] text-white text-sm"
                placeholder="9:00 AM - 6:00 PM or Closed"
              />
            </div>
          ))}
        </div>
      </motion.div>

      {/* Main CTA Button */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
        <h2 className="font-outfit font-semibold text-lg text-white mb-4">Main Call-to-Action Button</h2>
        <p className="text-sm text-[#71717A] mb-4">This button appears prominently on your profile</p>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-[#EDEDED]">Button Text</Label>
            <Input data-testid="cta-text-input" value={formData.cta_button.text} onChange={(e) => updateCTA('text', e.target.value)}
              className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="Contact Me" />
          </div>
          
          <div>
            <Label className="text-[#EDEDED]">Action</Label>
            <Select value={formData.cta_button.action} onValueChange={(v) => updateCTA('action', v)}>
              <SelectTrigger data-testid="cta-action-select" className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1E1E1E] border-[#27272A]">
                {CTA_ACTIONS.map((action) => (
                  <SelectItem key={action.id} value={action.id}>{action.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label className="text-[#EDEDED]">
              {formData.cta_button.action === 'call' ? 'Phone Number' : 
               formData.cta_button.action === 'email' ? 'Email Address' :
               formData.cta_button.action === 'whatsapp' ? 'WhatsApp Number' : 'URL'}
            </Label>
            <Input data-testid="cta-value-input" value={formData.cta_button.value} onChange={(e) => updateCTA('value', e.target.value)}
              className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" 
              placeholder={formData.cta_button.action === 'call' ? '+1234567890' : 
                          formData.cta_button.action === 'email' ? 'email@example.com' :
                          formData.cta_button.action === 'whatsapp' ? '+1234567890' : 'https://...'} />
          </div>
        </div>
        
        {/* Preview */}
        <div className="mt-6 p-4 rounded-xl bg-white/5">
          <p className="text-xs text-[#71717A] mb-2">Preview:</p>
          <Button className="w-full rounded-full bg-violet-500 hover:bg-violet-600 text-white py-6">
            {formData.cta_button.text || 'Contact Me'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default BusinessInfo;
